import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  ArrowLeft,
  ChevronRight,
  Shield,
  Home,
  Trash2,
  AlertTriangle,
  Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/store/auth';
import { supabase } from '@/lib/supabase';
import { currentType } from '@/lib/environment';
import { resendInvitation, revokeInvitation } from '@/lib/database';
import { WorkspacesListView } from './WorkspacesListView';
import { WorkspaceDetailView } from './WorkspaceDetailView';
import { AddMemberDialog, type DirectoryUser } from './AddMemberDialog';
import { EditMemberDialog } from './EditMemberDialog';

export interface WorkspaceSummary {
  id: string;
  name: string;
  subdomain: string;
  created_at: string;
  owner_email: string;
  project_count: number;
  document_count: number;
}

export interface ProjectWithCount {
  id: string;
  name: string;
  description: string;
  status: string;
  created_at: string;
  updated_at: string;
  document_count: number;
}

export interface Member {
  user_id: string;
  email: string;
  role: string;
  joined_at: string;
}

export interface WorkspaceInvitation {
  id: string;
  email: string;
  role: string;
  invited_at: string;
  expires_at: string | null;
  invited_by: string | null;
}

export interface WorkspaceDetails {
  workspace: {
    id: string;
    name: string;
    subdomain: string;
    created_at: string;
  };
  projects: ProjectWithCount[];
  members: Member[];
  invitations: WorkspaceInvitation[];
}

export interface UserWorkspace {
  id: string;
  name: string;
  role: string;
}

export interface UserSummary {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  workspace_count: number;
  workspaces: UserWorkspace[];
  subscription: {
    status: string;
    tier: string;
    has_stripe: boolean;
  } | null;
}

type View = 'workspaces' | 'workspace';

// Role a superadmin can assume when opening a project in a new tab.
type AssumeRole = 'admin' | 'reader';

export function SuperPanel() {
  const navigate = useNavigate();
  const { isSuperadmin, isLoading: authLoading } = useAuthStore();

  const [view, setView] = useState<View>('workspaces');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [workspaceDetails, setWorkspaceDetails] = useState<WorkspaceDetails | null>(null);

  // Breadcrumb state
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceSummary | null>(null);

  // Role-picker state: the project the superadmin chose to open, awaiting a
  // role selection before it opens in a new tab.
  const [projectToOpen, setProjectToOpen] = useState<ProjectWithCount | null>(null);

  // Delete dialog state
  const [showDeleteWorkspaceDialog, setShowDeleteWorkspaceDialog] = useState(false);
  const [showDeleteProjectsDialog, setShowDeleteProjectsDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());

  // Users state
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set());

  // Collapsible sections state
  const [workspacesSectionOpen, setWorkspacesSectionOpen] = useState(true);
  const [usersSectionOpen, setUsersSectionOpen] = useState(false);
  const [hideTestUsers, setHideTestUsers] = useState(true);

  // Add-member dialog + auth-user directory for autocomplete
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [directory, setDirectory] = useState<DirectoryUser[]>([]);
  const [pendingInvitationActionId, setPendingInvitationActionId] = useState<string | null>(null);

  // Edit-member dialog
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);

  // Fetch workspaces, users summary, and auth-user directory on mount
  useEffect(() => {
    if (!authLoading && isSuperadmin) {
      fetchWorkspacesSummary();
      fetchUsersSummary();
      fetchDirectory();
    }
  }, [authLoading, isSuperadmin]);

  const fetchWorkspacesSummary = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('super-panel-data', {
        body: { action: 'get-workspaces-summary', type: currentType() },
      });
      if (error) throw error;
      setWorkspaces(data.workspaces || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDirectory = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('super-panel-data', {
        body: { action: 'list-all-users', type: currentType() },
      });
      if (error) throw error;
      setDirectory(data?.users || []);
    } catch (err) {
      // Non-fatal: autocomplete just won't have suggestions.
      console.warn('Failed to load user directory', err);
    }
  };

  const fetchUsersSummary = async () => {
    setIsLoadingUsers(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('super-panel-data', {
        body: { action: 'get-users-summary', type: currentType() },
      });
      if (error) throw error;
      setUsers(data.users || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchWorkspaceDetails = async (workspace: WorkspaceSummary) => {
    setIsLoading(true);
    setError(null);
    setSelectedWorkspace(workspace);
    try {
      const { data, error } = await supabase.functions.invoke('super-panel-data', {
        body: { action: 'get-workspace-details', workspaceId: workspace.id, type: currentType() },
      });
      if (error) throw error;
      setWorkspaceDetails(data);
      setView('workspace');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workspace details');
    } finally {
      setIsLoading(false);
    }
  };

  // Open a project the same way its own workspace members see it, in a new tab,
  // assuming the chosen role. The target workspace + role ride along as query
  // params; the opened tab establishes an ephemeral impersonation from them
  // (see WorkspaceGuard) without disturbing this panel tab's session.
  const openProjectAs = (project: ProjectWithCount, role: AssumeRole) => {
    if (!selectedWorkspace) return;
    const url = `/projects/${project.id}?assumeWorkspace=${encodeURIComponent(
      selectedWorkspace.id
    )}&assumeRole=${role}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setProjectToOpen(null);
  };

  const goBack = () => {
    if (view === 'workspace') {
      setView('workspaces');
      setWorkspaceDetails(null);
      setSelectedWorkspace(null);
      setSelectedProjectIds(new Set());
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!selectedWorkspace) return;

    setIsDeleting(true);
    setError(null);
    try {
      const { error } = await supabase.functions.invoke('super-panel-data', {
        body: { action: 'delete-workspace', workspaceId: selectedWorkspace.id, type: currentType() },
      });
      if (error) throw error;

      // Close dialog and go back to workspaces list
      setShowDeleteWorkspaceDialog(false);
      setDeleteConfirmation('');
      setView('workspaces');
      setWorkspaceDetails(null);
      setSelectedWorkspace(null);

      // Refresh workspaces list
      await fetchWorkspacesSummary();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete workspace');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteProjects = async () => {
    if (selectedProjectIds.size === 0 || !workspaceDetails) return;

    setIsDeleting(true);
    setError(null);
    try {
      const { error } = await supabase.functions.invoke('super-panel-data', {
        body: {
          action: 'delete-projects',
          projectIds: Array.from(selectedProjectIds),
          type: currentType()
        },
      });
      if (error) throw error;

      // Close dialog and refresh workspace details
      setShowDeleteProjectsDialog(false);
      setDeleteConfirmation('');
      setSelectedProjectIds(new Set());

      // Refresh workspace details
      if (selectedWorkspace) {
        await fetchWorkspaceDetails(selectedWorkspace);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete projects');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvitation = async (invitation: WorkspaceInvitation) => {
    if (!selectedWorkspace) return;
    setPendingInvitationActionId(invitation.id);
    setError(null);
    try {
      await resendInvitation(selectedWorkspace.id, invitation.id);
      await fetchWorkspaceDetails(selectedWorkspace);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setPendingInvitationActionId(null);
    }
  };

  const handleRevokeInvitation = async (invitation: WorkspaceInvitation) => {
    if (!selectedWorkspace) return;
    if (!confirm(`Revoke invitation for ${invitation.email}?`)) return;
    setPendingInvitationActionId(invitation.id);
    setError(null);
    try {
      await revokeInvitation(selectedWorkspace.id, invitation.id);
      await fetchWorkspaceDetails(selectedWorkspace);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invitation');
    } finally {
      setPendingInvitationActionId(null);
    }
  };

  const toggleProjectSelection = (projectId: string) => {
    setSelectedProjectIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const toggleUserExpanded = (userId: string) => {
    setExpandedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setIsDeleting(true);
    setError(null);
    try {
      const { error } = await supabase.functions.invoke('super-panel-data', {
        body: { action: 'delete-user', userId: selectedUser.id, type: currentType() },
      });
      if (error) throw error;

      // Close dialog and refresh
      setShowDeleteUserDialog(false);
      setDeleteConfirmation('');
      setSelectedUser(null);

      // Refresh both users and workspaces lists
      await Promise.all([fetchUsersSummary(), fetchWorkspacesSummary()]);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const getDeleteWorkspaceConfirmText = () => {
    return `delete ${selectedWorkspace?.name}`;
  };

  const getDeleteProjectsConfirmText = () => {
    return `delete ${selectedProjectIds.size} project${selectedProjectIds.size > 1 ? 's' : ''}`;
  };

  const getDeleteUserConfirmText = () => {
    return `delete ${selectedUser?.email}`;
  };

  const isTestUser = (email: string) => /^test-.*@test\.ingesta\.dev$/.test(email);

  const filteredUsers = hideTestUsers
    ? users.filter(u => !isTestUser(u.email))
    : users;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600" />
      </div>
    );
  }

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              This page is only accessible to superadmins.
            </p>
            <Button onClick={() => navigate('/home')}>
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              {view !== 'workspaces' && (
                <Button variant="ghost" size="sm" onClick={goBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Shield className="h-8 w-8 text-amber-500" />
                Super Admin Panel
              </h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/home')}
            >
              <Home className="h-4 w-4 mr-2" />
              Home
            </Button>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center text-sm text-gray-500">
            <button
              onClick={() => { setView('workspaces'); setWorkspaceDetails(null); }}
              className="hover:text-gray-900"
            >
              All Workspaces
            </button>
            {selectedWorkspace && (
              <>
                <ChevronRight className="h-4 w-4 mx-2" />
                <button
                  onClick={() => { setView('workspace'); }}
                  className="hover:text-gray-900"
                >
                  {selectedWorkspace.name}
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline">
              Dismiss
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-ingesta-blue-600" />
          </div>
        ) : (
          <>
            {/* Workspaces List View */}
            {view === 'workspaces' && (
              <WorkspacesListView
                workspaces={workspaces}
                users={users}
                filteredUsers={filteredUsers}
                isLoadingUsers={isLoadingUsers}
                hideTestUsers={hideTestUsers}
                setHideTestUsers={setHideTestUsers}
                usersSectionOpen={usersSectionOpen}
                setUsersSectionOpen={setUsersSectionOpen}
                workspacesSectionOpen={workspacesSectionOpen}
                setWorkspacesSectionOpen={setWorkspacesSectionOpen}
                expandedUserIds={expandedUserIds}
                toggleUserExpanded={toggleUserExpanded}
                onSelectUser={(user) => {
                  setSelectedUser(user);
                  setShowDeleteUserDialog(true);
                }}
                onWorkspaceClick={fetchWorkspaceDetails}
                formatDate={formatDate}
              />
            )}

            {/* Workspace Details View */}
            {view === 'workspace' && workspaceDetails && (
              <WorkspaceDetailView
                workspaceDetails={workspaceDetails}
                selectedProjectIds={selectedProjectIds}
                toggleProjectSelection={toggleProjectSelection}
                onDeleteWorkspace={() => setShowDeleteWorkspaceDialog(true)}
                onDeleteProjects={() => setShowDeleteProjectsDialog(true)}
                onProjectClick={(project) => setProjectToOpen(project)}
                onAddMember={() => setShowAddMemberDialog(true)}
                onEditMember={(member) => {
                  setEditingMember(member);
                  setShowEditMemberDialog(true);
                }}
                onResendInvitation={handleResendInvitation}
                onRevokeInvitation={handleRevokeInvitation}
                pendingInvitationActionId={pendingInvitationActionId}
                formatDate={formatDate}
              />
            )}
          </>
        )}
      </div>

      {/* Edit Member Dialog */}
      {workspaceDetails && (
        <EditMemberDialog
          open={showEditMemberDialog}
          onOpenChange={(open) => {
            setShowEditMemberDialog(open);
            if (!open) setEditingMember(null);
          }}
          workspaceId={workspaceDetails.workspace.id}
          workspaceName={workspaceDetails.workspace.name}
          member={editingMember}
          totalAdmins={
            workspaceDetails.members.filter((m) => m.role === 'admin').length
          }
          onUpdated={async () => {
            if (selectedWorkspace) {
              await fetchWorkspaceDetails(selectedWorkspace);
            }
          }}
        />
      )}

      {/* Add Member Dialog */}
      {workspaceDetails && (
        <AddMemberDialog
          open={showAddMemberDialog}
          onOpenChange={setShowAddMemberDialog}
          workspaceId={workspaceDetails.workspace.id}
          workspaceName={workspaceDetails.workspace.name}
          directory={directory}
          existingMemberIds={
            new Set(workspaceDetails.members.map((m) => m.user_id))
          }
          pendingInviteEmails={
            new Set(
              (workspaceDetails.invitations || []).map((i) =>
                i.email.toLowerCase()
              )
            )
          }
          onAdded={async () => {
            if (selectedWorkspace) {
              await fetchWorkspaceDetails(selectedWorkspace);
            }
          }}
        />
      )}

      {/* Open-project role picker. Choosing a role opens the real project view
          in a new tab, impersonating that role for the project's workspace. */}
      <Dialog open={!!projectToOpen} onOpenChange={(open) => !open && setProjectToOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Open “{projectToOpen?.name}”</DialogTitle>
            <DialogDescription>
              Open this project in a new tab as a member of{' '}
              {selectedWorkspace?.name ?? 'the workspace'} would see it. Choose which role to
              assume. Your super-admin session in this tab is unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start gap-2">
            <Button
              variant="outline"
              onClick={() => projectToOpen && openProjectAs(projectToOpen, 'reader')}
            >
              Open as Reader
            </Button>
            <Button
              onClick={() => projectToOpen && openProjectAs(projectToOpen, 'admin')}
            >
              Open as Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Confirmation Dialog */}
      <Dialog open={showDeleteWorkspaceDialog} onOpenChange={setShowDeleteWorkspaceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Workspace
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the workspace
              <strong> "{selectedWorkspace?.name}"</strong> and all its projects, documents, and data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm">
              Type <code className="bg-gray-100 px-2 py-1 rounded font-mono">{getDeleteWorkspaceConfirmText()}</code> to confirm:
            </p>
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={getDeleteWorkspaceConfirmText()}
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteWorkspaceDialog(false);
                setDeleteConfirmation('');
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteWorkspace}
              disabled={deleteConfirmation !== getDeleteWorkspaceConfirmText() || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Workspace
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Projects Confirmation Dialog */}
      <Dialog open={showDeleteProjectsDialog} onOpenChange={setShowDeleteProjectsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedProjectIds.size} Project{selectedProjectIds.size > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the selected project{selectedProjectIds.size > 1 ? 's' : ''} and all associated documents and data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-sm font-medium mb-2">Projects to delete:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                {workspaceDetails?.projects
                  .filter(p => selectedProjectIds.has(p.id))
                  .map(p => (
                    <li key={p.id}>• {p.name}</li>
                  ))
                }
              </ul>
            </div>
            <p className="text-sm">
              Type <code className="bg-gray-100 px-2 py-1 rounded font-mono">{getDeleteProjectsConfirmText()}</code> to confirm:
            </p>
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={getDeleteProjectsConfirmText()}
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteProjectsDialog(false);
                setDeleteConfirmation('');
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteProjects}
              disabled={deleteConfirmation !== getDeleteProjectsConfirmText() || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Project{selectedProjectIds.size > 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the user and:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium mb-2">
                <Mail className="h-4 w-4 inline mr-2" />
                {selectedUser?.email}
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-6">
                <li>• Delete all workspaces where they're the only admin</li>
                <li>• Remove from other workspaces</li>
                {selectedUser?.subscription?.has_stripe && (
                  <>
                    <li>• Cancel Stripe subscription</li>
                    <li>• Delete Stripe customer</li>
                  </>
                )}
              </ul>
            </div>
            {selectedUser && selectedUser.workspace_count > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  This user has {selectedUser.workspace_count} workspace{selectedUser.workspace_count > 1 ? 's' : ''}.
                  Workspaces where they're the only admin will be deleted.
                </p>
              </div>
            )}
            <p className="text-sm">
              Type <code className="bg-gray-100 px-2 py-1 rounded font-mono">{getDeleteUserConfirmText()}</code> to confirm:
            </p>
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={getDeleteUserConfirmText()}
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteUserDialog(false);
                setDeleteConfirmation('');
                setSelectedUser(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteConfirmation !== getDeleteUserConfirmText() || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
